import {
  baseCdcEventDataSchema,
  cdcAuthContextSchema,
  cdcEventModule,
  cdcEventType,
} from '@tempo-io/common-dto';
import { discriminatedUnion, z } from 'zod';
import { creditEventDataChangeDTOSchema } from './credit-event-data-change-dto';
import { organizationDTOSchema } from './organization-dto';
import { subscriptionDTOSchema } from './subscription-dto';
import { roleSchema, userDTOSchema } from './user-dto';

export const membershipDataChangePayloadSchema = z.object({
  userId: z.string(),
  organizationId: z.string(),
  role: roleSchema,
});

// Entity identifiers for delete events
export const userIdentifierSchema = z.object({
  id: z.string(),
});

export const organizationIdentifierSchema = z.object({
  id: z.string(),
  requestId: z.string().optional(),
});

export const membershipIdentifierSchema = z.object({
  userId: z.string(),
  organizationId: z.string(),
});

const idModuleSchema = z.object({
  module: z.literal(cdcEventModule.enum.id),
});

export const userDataChangeDTOSchema = discriminatedUnion('eventType', [
  idModuleSchema.extend({
    entityType: z.literal('user'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: userDTOSchema,
  }),
  idModuleSchema.extend({
    entityType: z.literal('user'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: userDTOSchema,
  }),
  idModuleSchema.extend({
    entityType: z.literal('user'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: userIdentifierSchema,
  }),
]);

export const organizationDataChangeDTOSchema = discriminatedUnion('eventType', [
  idModuleSchema.extend({
    entityType: z.literal('organization'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: organizationDTOSchema,
  }),
  idModuleSchema.extend({
    entityType: z.literal('organization'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: organizationDTOSchema,
  }),
  idModuleSchema.extend({
    entityType: z.literal('organization'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: organizationIdentifierSchema,
  }),
]);

export const subscriptionDataChangeDTOSchema = discriminatedUnion('eventType', [
  idModuleSchema.extend({
    entityType: z.literal('subscription'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: subscriptionDTOSchema,
  }),
  idModuleSchema.extend({
    entityType: z.literal('subscription'),
    eventType: z.literal(cdcEventType.enum.updated_partial),
    entity: subscriptionDTOSchema,
    previousEntity: subscriptionDTOSchema,
  }),
]);

export const membershipDataChangeDTOSchema = discriminatedUnion('eventType', [
  idModuleSchema.extend({
    entityType: z.literal('membership'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: membershipDataChangePayloadSchema,
  }),
  idModuleSchema.extend({
    entityType: z.literal('membership'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: membershipDataChangePayloadSchema,
  }),
  idModuleSchema.extend({
    entityType: z.literal('membership'),
    eventType: z.literal(cdcEventType.enum.updated_partial),
    entity: membershipDataChangePayloadSchema,
    previousEntity: membershipDataChangePayloadSchema,
  }),
  idModuleSchema.extend({
    entityType: z.literal('membership'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: membershipIdentifierSchema,
  }),
]);

export const inviteDataChangePayloadSchema = z.object({
  id: z.number(),
  auth0Id: z.string(),
  inviterUserId: z.string(),
  inviteeUserId: z.string().nullable(),
  inviteeEmail: z.string(),
  organizationId: z.string(),
  role: roleSchema,
  expiresAt: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const inviteIdentifierSchema = z.object({
  id: z.number(),
  organizationId: z.string(),
});

export const inviteDataChangeDTOSchema = discriminatedUnion('eventType', [
  idModuleSchema.extend({
    entityType: z.literal('invite'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: inviteDataChangePayloadSchema,
  }),
  idModuleSchema.extend({
    entityType: z.literal('invite'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: inviteDataChangePayloadSchema,
  }),
  idModuleSchema.extend({
    entityType: z.literal('invite'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: inviteIdentifierSchema,
  }),
]);

export const domainVerificationDataChangePayloadSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  domain: z.string(),
  status: z.enum(['pending', 'verified']),
  verifiedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const domainVerificationIdentifierSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
});

export const domainVerificationDataChangeDTOSchema = discriminatedUnion('eventType', [
  idModuleSchema.extend({
    entityType: z.literal('domainVerification'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: domainVerificationDataChangePayloadSchema,
  }),
  idModuleSchema.extend({
    entityType: z.literal('domainVerification'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: domainVerificationDataChangePayloadSchema,
  }),
  idModuleSchema.extend({
    entityType: z.literal('domainVerification'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: domainVerificationIdentifierSchema,
  }),
]);

export const samlConnectionDataChangePayloadSchema = z.object({
  id: z.number(),
  organizationId: z.string(),
  auth0Id: z.string().nullable(),
  displayName: z.string(),
  domain: z.string(),
  isConfigured: z.boolean(),
  isDomainConnection: z.boolean(),
  idpInitiatedLoginEnabled: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const samlConnectionIdentifierSchema = z.object({
  id: z.number(),
  organizationId: z.string(),
});

export const samlConnectionDataChangeDTOSchema = discriminatedUnion('eventType', [
  idModuleSchema.extend({
    entityType: z.literal('samlConnection'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: samlConnectionDataChangePayloadSchema,
  }),
  idModuleSchema.extend({
    entityType: z.literal('samlConnection'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: samlConnectionDataChangePayloadSchema,
  }),
  idModuleSchema.extend({
    entityType: z.literal('samlConnection'),
    eventType: z.literal(cdcEventType.enum.updated_partial),
    entity: samlConnectionDataChangePayloadSchema,
    previousEntity: samlConnectionDataChangePayloadSchema,
  }),
  idModuleSchema.extend({
    entityType: z.literal('samlConnection'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: samlConnectionIdentifierSchema,
  }),
]);

export const oauthClientDataChangePayloadSchema = z.object({
  id: z.string(),
  auth0Id: z.string(),
  organizationId: z.string(),
  displayName: z.string(),
  description: z.string().nullable(),
  appType: z.enum(['regular_web', 'spa']),
  callbackUrls: z.array(z.string()),
  allowedLogoutUrls: z.array(z.string()),
  webOrigins: z.array(z.string()),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const oauthClientIdentifierSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
});

export const oauthClientDataChangeDTOSchema = discriminatedUnion('eventType', [
  idModuleSchema.extend({
    entityType: z.literal('oauthClient'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: oauthClientDataChangePayloadSchema,
  }),
  idModuleSchema.extend({
    entityType: z.literal('oauthClient'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: oauthClientDataChangePayloadSchema,
  }),
  idModuleSchema.extend({
    entityType: z.literal('oauthClient'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: oauthClientIdentifierSchema,
  }),
]);

// Compliance-proof audit record for a decommission request. Carries opaque identifiers
// and trigger metadata only — never the PII a decommission removed.
export const decommissionRequestDataChangePayloadSchema = z.object({
  id: z.string(),
  organizationId: z.string().nullable(),
  userId: z.string().nullable(),
  trigger: z.enum([
    'mn_webhook',
    'org_admin_ui',
    'self_service',
    'admin_on_behalf',
    'trial_expired',
  ]),
  requestedAt: z.string(),
  completedAt: z.string().optional(),
  targetsCompleted: z.array(z.string()).optional(),
});

export const decommissionRequestDataChangeDTOSchema = discriminatedUnion('eventType', [
  idModuleSchema.extend({
    entityType: z.literal('decommissionRequest'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: decommissionRequestDataChangePayloadSchema,
  }),
  idModuleSchema.extend({
    entityType: z.literal('decommissionRequest'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: decommissionRequestDataChangePayloadSchema,
  }),
]);

export const idDataChangeDTOSchema = z.object({
  data: baseCdcEventDataSchema.and(
    z.discriminatedUnion('entityType', [
      userDataChangeDTOSchema,
      organizationDataChangeDTOSchema,
      membershipDataChangeDTOSchema,
      subscriptionDataChangeDTOSchema,
      inviteDataChangeDTOSchema,
      domainVerificationDataChangeDTOSchema,
      samlConnectionDataChangeDTOSchema,
      oauthClientDataChangeDTOSchema,
      creditEventDataChangeDTOSchema,
      decommissionRequestDataChangeDTOSchema,
    ]),
  ),
  authContext: cdcAuthContextSchema,
  metadata: z.record(z.string(), z.string()).optional(),
});

export type IdDataChangeDTO = z.output<typeof idDataChangeDTOSchema>;
export type UserDataChangeDTO = Extract<IdDataChangeDTO, { entityType: 'user' }>;
export type OrganizationDataChangeDTO = Extract<IdDataChangeDTO, { entityType: 'organization' }>;
export type MembershipDataChangeDTO = Extract<IdDataChangeDTO, { entityType: 'membership' }>;
export type SubscriptionDataChangeDTO = Extract<IdDataChangeDTO, { entityType: 'subscription' }>;
export type InviteDataChangeDTO = Extract<IdDataChangeDTO, { entityType: 'invite' }>;
export type DomainVerificationDataChangeDTO = Extract<
  IdDataChangeDTO,
  { entityType: 'domainVerification' }
>;
export type SamlConnectionDataChangeDTO = Extract<
  IdDataChangeDTO,
  { entityType: 'samlConnection' }
>;
export type OauthClientDataChangeDTO = Extract<IdDataChangeDTO, { entityType: 'oauthClient' }>;
export type CreditEventDataChangeDTO = Extract<IdDataChangeDTO, { entityType: 'credit_event' }>;
export type DecommissionRequestDataChangeDTO = Extract<
  IdDataChangeDTO,
  { entityType: 'decommissionRequest' }
>;
